'use client';

import { useEffect, useState, useMemo } from 'react';
import { getUrlParameter, hasUrlParameter, setQueryParam, deleteQueryParam } from '@/lib/utils/url-params';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/Icons/Icons';

interface PaginationProps {
  pageCount: number;
  limit: number;
  action: (pageIndex: number) => Promise<void>;
  offsetTop?: number;
  /** Current page from URL (e.g. searchParams) so pagination resets when filter/search change */
  currentPageFromUrl?: number;
}

type PageItem = number | string;

/**
 * Generate pagination range with ellipsis - fewer pages when many total so it fits without scroll
 * @param current - Current page number
 * @param total - Total number of pages
 * @returns Array of page numbers and ellipsis strings
 */
function generatePaginationRange(current: number, total: number): PageItem[] {
  const delta = total > 5 ? 1 : 2; // More ellipses when many pages → fits on mobile without scroll
  const left = current - delta;
  const right = current + delta + 1;
  const range: number[] = [];
  const rangeWithDots: PageItem[] = [];

  // Collect pages to show
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= left && i < right)) {
      range.push(i);
    }
  }

  // Add ellipsis where needed
  let lastPage: number | undefined;
  for (const page of range) {
    if (lastPage !== undefined) {
      if (page - lastPage === 2) {
        rangeWithDots.push(lastPage + 1);
      } else if (page - lastPage !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(page);
    lastPage = page;
  }

  return rangeWithDots;
}

/**
 * Navigation button component for pagination
 */
interface NavButtonProps {
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
  icon: React.ReactNode;
}

function NavButton({ onClick, disabled, ariaLabel, icon }: NavButtonProps) {
  const baseClasses = 'p-4 bg-surface-3 cs-s-body flex items-center shrink-0';
  const enabledClasses = 'text-text-default-medium hover:text-text-default-strong transition-colors cursor-pointer';
  const disabledClasses = 'text-text-default-light cursor-not-allowed';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`${baseClasses} ${disabled ? disabledClasses : enabledClasses}`}
    >
      {icon}
    </button>
  );
}

/**
 * Page number button component
 */
interface PageButtonProps {
  page: number;
  isActive: boolean;
  onClick: () => void;
}

function PageButton({ page, isActive, onClick }: PageButtonProps) {
  const baseClasses = 'px-[19.5px] py-[13.5px] cs-s-body-light transition-colors';
  const activeClasses = 'bg-amethyst-accent text-[var(--Color-Primary-Shadow-Heavy)] font-medium';
  const inactiveClasses = 'text-text-default-strong hover:bg-surface-4 cursor-pointer';

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
    >
      {page}
    </button>
  );
}

function getInitialPage(): number {
  if (typeof window === 'undefined') return 1;
  return hasUrlParameter('page') ? parseInt(getUrlParameter('page') || '1', 10) : 1;
}

export function Pagination({ pageCount, limit, action, offsetTop = 0, currentPageFromUrl }: PaginationProps) {
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const totalPages = Math.ceil(pageCount / limit);

  // Sync from URL on mount (e.g. when key changes after filter/search reset)
  useEffect(() => {
    setCurrentPage(getInitialPage());
  }, [offsetTop]);

  // When parent resets (filter/search change), sync to that page
  useEffect(() => {
    if (currentPageFromUrl !== undefined) {
      setCurrentPage(currentPageFromUrl);
    }
  }, [currentPageFromUrl]);

  // Handle page navigation
  const handlePageChange = async (pageIndex: number | string) => {
    const index = typeof pageIndex === 'string' && pageIndex !== '' 
      ? parseInt(pageIndex, 10) 
      : typeof pageIndex === 'number' 
        ? pageIndex 
        : 1;

    if (isNaN(index) || index < 1 || index > totalPages) return;

    // Update UI immediately so page change is visible
    setCurrentPage(index);

    if (typeof window !== 'undefined') {
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }

    // Update URL so the action can use router or parent can read from URL
    if (index === 1) {
      deleteQueryParam('page', false, false);
    } else {
      setQueryParam('page', index, false, false);
    }

    await action(index);
  };

  // Memoize pagination range
  const pages = useMemo(() => generatePaginationRange(currentPage, totalPages), [currentPage, totalPages]);

  // Calculate previous and next page numbers
  const prevPage = currentPage > 1 ? currentPage - 1 : null;
  const nextPage = currentPage < totalPages ? currentPage + 1 : null;

  // Don't render if only one page or less
  if (totalPages <= 1) return null;

  // Render navigation button with icon
  const renderNavButton = (
    direction: 'prev' | 'next',
    page: number | null,
    isDisabled: boolean
  ) => {
    const Icon = direction === 'prev' ? ChevronLeftIcon : ChevronRightIcon;
    const iconColor = isDisabled ? 'text-icon-soft' : 'text-icon-strong';
    const ariaLabel = direction === 'prev' ? 'Previous page' : 'Next page';

    return (
      <NavButton
        onClick={() => page !== null && handlePageChange(page)}
        disabled={isDisabled}
        ariaLabel={ariaLabel}
        icon={<Icon size={16} className={iconColor} />}
      />
    );
  };

  const paginationList = (
    <ul className="flex items-center justify-center gap-1">
      <li>{renderNavButton('prev', prevPage, currentPage === 1)}</li>
      {pages.map((page, index) => (
        <li key={`${page}-${index}`}>
          {page === '...' ? (
            <span className="px-[19.5px] py-[13.5px] cs-s-body text-text-default-strong">...</span>
          ) : (
            <PageButton
              page={page as number}
              isActive={currentPage === page}
              onClick={() => handlePageChange(page)}
            />
          )}
        </li>
      ))}
      <li>{renderNavButton('next', nextPage, currentPage >= totalPages)}</li>
    </ul>
  );

  return (
    <div className="w-full flex justify-center">
      <nav aria-label="Page navigation" data-swiftype-index="false">
        {paginationList}
      </nav>
    </div>
  );
}

